import { useCallback, useEffect, useMemo, useState } from 'react'

import { F0Dialog, UpsellRequestResponseDialog } from '@factorialco/f0-react'
import { AvailableModules } from 'generated/featureDefinitions'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track } from 'modules/core/lib/Tracking/tracker'
import i18n from 'modules/core/lib/i18n'

import { isValidModule } from 'modules/marketplace_modules/lib/typesValidation'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { events } from 'modules/upsell/lib/events'
import { useCreateUpsellRequestWithErrorHandling } from 'modules/upselling_kit/hooks/useCreateUpsellRequest'
import { readLatestUpsellOrigin } from 'modules/upselling_kit/utils/localStorage'

import { CloseMeeting } from './_CloseMeeting'

interface UpsellMeetingProps {
  isOpen: boolean
  onClose: () => void
  meetingUrl: string
  trackingSource?: string
  moduleSlug?: string
  moduleRequested?: AvailableModules
  requestSource?: string
  pageSource?: string
  upsellRequestStatus?: string
  owner?: string
  skipCloseModal?: boolean
  upsellRequestType?: string
}

export const UpsellMeeting = ({
  isOpen = true,
  onClose,
  meetingUrl,
  trackingSource = 'upsell_meeting_modal',
  moduleSlug,
  moduleRequested,
  requestSource = 'From marketplace meeting booking',
  pageSource = window.location.pathname,
  owner,
  skipCloseModal = false,
  upsellRequestType = 'Book a meeting',
}: UpsellMeetingProps) => {
  const validChargebeeModuleName = isValidModule(moduleRequested) ? moduleRequested : undefined
  const { access, email, employee, company } = useCurrentSession()
  const [hasBookedMeeting, setHasBookedMeeting] = useState(false)
  const [showCloseMeetingModal, setShowCloseMeetingModal] = useState(false)
  const [upsellDialogOpen, setUpsellDialogOpen] = useState(false)
  const [upsellSuccess, setUpsellSuccess] = useState(false)

  const autofilledMeetingUrl = useMemo(() => {
    if (!meetingUrl || !access || !email) {
      return meetingUrl
    }

    try {
      const url = new URL(meetingUrl)

      url.searchParams.set('firstName', access.firstName || '')
      url.searchParams.set('lastName', access.lastName || '')
      url.searchParams.set('email', email || '')
      if (employee?.phoneNumber) {
        url.searchParams.set('phone', employee.phoneNumber)
      }
      if (company?.name) {
        url.searchParams.set('company', company.name)
      }

      return url.toString()
    } catch (error) {
      return meetingUrl
    }
  }, [meetingUrl, access, email, employee, company])

  const { create } = useCreateUpsellRequestWithErrorHandling()

  const executeUpsellRequest = useCallback(async () => {
    const latestUpsellOrigin = readLatestUpsellOrigin()
    const currentCategory = latestUpsellOrigin?.request_source_category || 'Organic'
    const currentComponent = latestUpsellOrigin?.request_source_component
    const currentRequestSource = latestUpsellOrigin?.request_source || requestSource

    try {
      await create({
        moduleRequested: validChargebeeModuleName,
        requestSource: `book-a-meeting_${currentRequestSource}`,
        requestSourceCategory: currentCategory,
        requestSourceComponent: currentComponent,
        requestReason: ValidReasons.MissingAddon,
        pageSource,
        upsellRequestStatus: 'Meeting booked completed',
        owner,
        upsellRequestComponentType: 'hubspot_iframe_meeting_booking',
        upsellRequestType,
      })
    } catch (error) {}
  }, [create, validChargebeeModuleName, requestSource, pageSource, owner, upsellRequestType])

  const handleClose = () => {
    track(events.closeBookAMeeting, {
      source: trackingSource,
      module: moduleSlug,
      moduleRequested: validChargebeeModuleName,
      requestSource,
      pageSource,
      hasBookedMeeting: hasBookedMeeting ? 'yes' : 'no',
    })

    if (!hasBookedMeeting && !skipCloseModal) {
      setShowCloseMeetingModal(true)
    } else {
      onClose()
    }
  }

  const handleCloseMeetingModal = () => {
    setShowCloseMeetingModal(false)
    onClose()
  }

  const handleUpsellRequestComplete = (success: boolean) => {
    setUpsellSuccess(success)
    setUpsellDialogOpen(true)
  }

  const handleUpsellDialogClose = () => {
    setUpsellDialogOpen(false)
    setUpsellSuccess(false)
  }

  useEffect(() => {
    if (isOpen) {
      setHasBookedMeeting(false)
      track(events.openBookAMeeting, {
        source: trackingSource,
        module: moduleSlug,
        moduleRequested: validChargebeeModuleName,
        requestSource,
        pageSource,
      })
    }
  }, [isOpen, trackingSource, moduleSlug, validChargebeeModuleName, requestSource, pageSource])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://meetings.hubspot.com') {
        return
      }
      if (event.data?.meetingBookSucceeded) {
        setHasBookedMeeting(true)
        track(events.bookAMeeting, {
          source: trackingSource,
          module: moduleSlug,
          moduleRequested: validChargebeeModuleName,
          requestSource,
          pageSource,
        })
        executeUpsellRequest()
      }
    }
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [
    onClose,
    trackingSource,
    moduleSlug,
    executeUpsellRequest,
    validChargebeeModuleName,
    requestSource,
    pageSource,
  ])

  return (
    <>
      <F0Dialog
        isOpen={isOpen}
        onClose={handleClose}
        position="fullscreen"
        title={i18n.t('upselling_kit.book_a_meeting.book_a_meeting')}
      >
        <iframe
          src={autofilledMeetingUrl}
          width="100%"
          height="100%"
          frameBorder="0"
          style={{
            height: 'calc(100vh - 100px)',
            minHeight: '600px',
          }}
        />
      </F0Dialog>
      <CloseMeeting
        isOpen={showCloseMeetingModal}
        onClose={handleCloseMeetingModal}
        onUpsellRequestComplete={handleUpsellRequestComplete}
        moduleRequested={moduleRequested}
        requestSource={requestSource}
        pageSource={pageSource}
        owner={owner}
      />
      <UpsellRequestResponseDialog
        open={upsellDialogOpen}
        success={upsellSuccess}
        portalContainer={document.getElementById('root')}
        errorMessage={{
          title: i18n.t('upselling_kit.upselling_button.error_message.title'),
          description: i18n.t('upselling_kit.upselling_button.error_message.description'),
        }}
        successMessage={{
          title: i18n.t('upselling_kit.upselling_button.success_message.title_2'),
          description: i18n.t('upselling_kit.upselling_button.success_message.description'),
        }}
        closeLabel={i18n.t('upselling_kit.upselling_button.close_label')}
        onClose={handleUpsellDialogClose}
      />
    </>
  )
}
